/**
 * Change order public API client (WU-14) — thin fetch wrappers over the
 * tokenized change-order endpoints, every response parsed through the SHARED
 * zod schemas. Mirrors api.ts (same-origin `/api/v1`, same ApiError) without
 * modifying it: its request helper is module-private, so a minimal copy
 * lives here.
 */
import {
  PublicChangeOrderAcceptResponseSchema,
  PublicChangeOrderConsentResponseSchema,
  PublicChangeOrderDeclineResponseSchema,
  PublicChangeOrderViewSchema,
} from '@buildledger/shared';
import type {
  PublicChangeOrderAcceptRequest,
  PublicChangeOrderAcceptResponse,
  PublicChangeOrderConsentResponse,
  PublicChangeOrderDeclineRequest,
  PublicChangeOrderDeclineResponse,
  PublicChangeOrderView,
} from '@buildledger/shared';

import { ApiError } from './api';

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError(0, 'Could not reach the server — check your connection and try again.');
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? Array.isArray((body as { message: unknown }).message)
          ? ((body as { message: string[] }).message[0] ?? res.statusText)
          : String((body as { message: unknown }).message)
        : res.statusText;
    throw new ApiError(res.status, message);
  }
  return body;
}

function tokenPath(token: string): string {
  return `/public/change-orders/${encodeURIComponent(token)}`;
}

export function getChangeOrder(token: string): Promise<PublicChangeOrderView> {
  return request(tokenPath(token)).then((body) => PublicChangeOrderViewSchema.parse(body));
}

export function postChangeOrderConsent(token: string): Promise<PublicChangeOrderConsentResponse> {
  return request(`${tokenPath(token)}/consent`, {
    method: 'POST',
    body: JSON.stringify({ agree: true }),
  }).then((body) => PublicChangeOrderConsentResponseSchema.parse(body));
}

export function postChangeOrderAccept(
  token: string,
  dto: PublicChangeOrderAcceptRequest,
): Promise<PublicChangeOrderAcceptResponse> {
  return request(`${tokenPath(token)}/accept`, {
    method: 'POST',
    body: JSON.stringify(dto),
  }).then((body) => PublicChangeOrderAcceptResponseSchema.parse(body));
}

export function postChangeOrderDecline(
  token: string,
  dto: PublicChangeOrderDeclineRequest,
): Promise<PublicChangeOrderDeclineResponse> {
  return request(`${tokenPath(token)}/decline`, {
    method: 'POST',
    body: JSON.stringify(dto),
  }).then((body) => PublicChangeOrderDeclineResponseSchema.parse(body));
}
